import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, User, LogOut, LayoutDashboard, Settings } from "lucide-react";
import brandIcon from "@/assets/brand-icon.png";
import logoFull from "@/assets/native-plant-garden-planner-logo.png";
import { Button } from "@/components/ui/button";
import { NavLink } from "@/components/NavLink";
import { BRAND } from "@/lib/brand";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { user, profile, signOut, loading, displayName } = useAuth();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { label: "How It Works", href: "/how-it-works" },
    { label: "What You Get", href: "/what-you-get" },
    { label: "Pricing", href: "/pricing" },
    { label: "Ecosystem", href: "/ecosystem" },
    { label: "Gallery", href: "/gallery" },
    { label: "FAQ", href: "/faq" },
  ];

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-shadow duration-300 ${
        isScrolled ? "shadow-md border-border/60" : "border-border/40 shadow-none"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-[1280px] items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex shrink-0 items-center pl-4 mr-6 group">
          <img
            src={logoFull}
            alt={BRAND.APP_NAME}
            width={122}
            height={40}
            className="h-8 lg:h-10 w-auto object-contain transition-transform group-hover:scale-[1.02]"
            onError={(e) => {
              const target = e.currentTarget;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <span className="hidden items-center gap-2.5" style={{ display: 'none' }}>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 overflow-hidden">
              <img src={brandIcon} alt="" className="h-7 w-7 object-contain" />
            </div>
            <span className="font-display text-xl font-semibold text-foreground whitespace-nowrap hidden sm:inline">
              {BRAND.APP_NAME}
            </span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1 shrink-0">
          {navItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted whitespace-nowrap"
              activeClassName="text-primary bg-primary/10"
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Auth & CTA Buttons — Desktop */}
        <div className="hidden lg:flex items-center gap-3 shrink-0">
          {!loading && (
            <>
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <User className="h-4 w-4" />
                      {displayName}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="cursor-pointer">
                        <LayoutDashboard className="h-4 w-4 mr-2" />
                        My Plans
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/account" className="cursor-pointer">
                        <Settings className="h-4 w-4 mr-2" />
                        Account
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive">
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/auth">
                  <Button variant="ghost" size="sm">
                    Sign In
                  </Button>
                </Link>
              )}
            </>
          )}
          <Link to="/create">
            <Button variant="hero" size="sm">
              Create My Plan
            </Button>
          </Link>
        </div>

        {/* Mobile: CTA + Hamburger */}
        <div className="flex lg:hidden items-center gap-2">
          <Link to="/create">
            <Button variant="hero" size="sm" className="text-xs px-3">
              Create My Plan
            </Button>
          </Link>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden border-t border-border/40 bg-background">
          <nav className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-8 py-4 flex flex-col gap-2">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsMenuOpen(false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === item.href
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                {item.label}
              </Link>
            ))}

            {/* Mobile Auth */}
            {!loading && (
              <div className="pt-2 mt-2 border-t border-border/40 space-y-2">
                {user ? (
                  <>
                    <div className="px-4 py-2 text-sm text-muted-foreground">
                      Signed in as {displayName}
                    </div>
                    <Link to="/dashboard" onClick={() => setIsMenuOpen(false)}>
                      <Button variant="outline" className="w-full justify-start mb-2">
                        <LayoutDashboard className="h-4 w-4 mr-2" />
                        My Plans
                      </Button>
                    </Link>
                    <Link to="/account" onClick={() => setIsMenuOpen(false)}>
                      <Button variant="outline" className="w-full justify-start mb-2">
                        <Settings className="h-4 w-4 mr-2" />
                        Account
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        handleSignOut();
                        setIsMenuOpen(false);
                      }}
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <Link to="/auth" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="outline" className="w-full">
                      Sign In
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
